import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon';
import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppointmentListItem } from '@spike/appointments-list-model';
import clsx from 'clsx';
import ListPetInfo from 'components/Bookings2/UI/ListPetInfo/ListPetInfo';
import RoomSelector from 'components/Bookings2/UI/RoomSelector/RoomSelector';
import StatusButton from 'components/OrderDetail/StatusButton';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { usePermissions, useTimeZone } from 'hooks';
import { FunctionComponent, MouseEvent } from 'react';
import { useMediaQuery } from 'react-responsive';
import BoardingCheckMoment from './BoardingCheckMoment';
import styles from './BoardingTableRow.module.scss';
import PetParent from './PetParent';
import NotesTableRow from './NotesTableRow';
import PaidIcon from './PaidIcon';
import DepositPendingIcon from './DepositPendingIcon';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import Service from './Service';

interface BoardingTableRowProps {
    app: AppointmentListItem;
    isLast?: boolean;
    scrollerContainerId: string;
    className?: string;
    onCheckout?: () => void;
    onShowOrder?: () => void;
    onShowClient?: () => void;
    onShowPet?: () => void;
    onChangeRoomUnit?: (roomUnitId: number | null) => void;
    selected?: boolean;
    onToggleSelect?: (checked: boolean) => void;
}

export const BoardingTableRow: FunctionComponent<BoardingTableRowProps> = props => {
    const { isUserLoggedInMarketplace } = useOrganizationMarketplaces();
    const allowActions = isUserLoggedInMarketplace(props.app.marketplaceId);

    const permissions = usePermissions();
    const isCheckoutAllowed = permissions.checkout.takePayments;

    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const timezone = useTimeZone();

    const hasNotes = props.app.notes.trim().length > 0;

    const checkoutOption = {
        id: 'checkout',
        label: 'Checkout',
        icon: faMoneyBill,
        onClick: () => props.onCheckout?.()
    };

    const options = isCheckoutAllowed ? [checkoutOption] : [];

    const clickHandler = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        props.onShowOrder?.();
    };

    const date = props.app.startAt.tz(timezone);

    const petInfo = (
        <ListPetInfo
            orderId={props.app.orderId}
            pet={props.app.pet}
            onClick={allowActions ? props.onShowPet : undefined}
        />
    );

    const services = (
        <Service
            service={props.app.service}
            petId={props.app.pet.id}
            marketplaceId={props.app.marketplaceId}
        />
    );

    const room = (
        <RoomSelector
            petId={props.app.pet.id}
            serviceId={props.app.service.id}
            dropOffRoom={date.toISOString()}
            PickUpRoom={props.app.endAt.toISOString()}
            room={props.app.room}
            fullWidth={isMobile}
            scrollerContainerId={props.scrollerContainerId}
            editable={allowActions}
            onChange={props.onChangeRoomUnit}
        />
    );

    const statusButton = (
        <StatusButton
            appointmentId={props.app.appointmentId}
            apppointmentMarketplaceId={props.app.marketplaceId}
            appointmentStatus={props.app.status}
            appointmentClosedAt={props.app.closedAt ?? undefined}
            appointmentCancelationReasonId={props.app.cancellationReasonId ?? null}
            recurringUuid={props.app.recurringUuid ?? undefined}
            customerFirstName={props.app.customer.firstName}
            customerLastName={props.app.customer.lastName}
            reloadOrderAfterStatusChange={false}
            businessAreaId={props.app.service.businessArea}
            fullWidth={isMobile}
            scrollerContainerId={props.scrollerContainerId}
            position="fixed"
        />
    );

    const checkIn = (
        <BoardingCheckMoment
            moment={props.app.startAt}
            pendingDropOff={props.app.pendingDropOff}
        />
    );
    const checkOut = <BoardingCheckMoment moment={props.app.endAt} />;
    const nights = <span>{props.app.nights} nights</span>;

    const showCheckbox = props.onToggleSelect !== undefined;
    const columnCount = showCheckbox ? 11 : 10;

    const notMobileRow = (
        <>
            <TableRow
                className={clsx(styles.row, { [styles.withoutBorder]: hasNotes }, props.className)}
                onClick={clickHandler}
            >
                {showCheckbox && (
                    <TableCell
                        className={clsx(styles.cell, styles.checkboxCell)}
                        onClick={event => event.stopPropagation()}
                    >
                        <Checkbox
                            checked={props.selected}
                            onChange={props.onToggleSelect}
                        />
                    </TableCell>
                )}
                <TableCell className={clsx(styles.cell, styles.clientNameCell)}>
                    <PetParent
                        customer={props.app.customer}
                        marketplaceId={props.app.marketplaceId}
                        showOnly={!allowActions}
                        showTagsIcon={!isMobile}
                        onClick={props.onShowClient}
                    />
                </TableCell>
                <TableCell className={styles.cell}>{petInfo}</TableCell>
                <TableCell className={styles.cell}>{services}</TableCell>
                <TableCell className={clsx(styles.cell, styles.roomCell)}>{room}</TableCell>
                <TableCell className={styles.cell}>{checkIn}</TableCell>
                <TableCell className={styles.cell}>{checkOut}</TableCell>
                <TableCell className={clsx(styles.cell, styles.nighCell)}>{nights}</TableCell>
                <TableCell className={clsx(styles.cell, styles.priceCell)}>
                    ${Number(props.app.total).toFixed(2)}
                    <DepositPendingIcon show={props.app.bookingDepositPending} />
                    <PaidIcon paid={props.app.paid} />
                </TableCell>
                <TableCell
                    width={180}
                    className={clsx(styles.cell, styles.statusCell)}
                >
                    {statusButton}
                </TableCell>
                <TableCell
                    isActions
                    className={styles.cell}
                    onClick={event => event.stopPropagation()}
                >
                    {allowActions && (
                        <ThreeDotsDropdown
                            options={options}
                            direction={props.isLast ? 'up' : 'down'}
                        />
                    )}
                </TableCell>
            </TableRow>
            {hasNotes && (
                <NotesTableRow
                    notes={props.app.notes}
                    colSpan={columnCount}
                />
            )}
        </>
    );

    const mobileRow = (
        <TableRow
            className={clsx(styles.mobileRow, props.className)}
            onClick={clickHandler}
        >
            <TableCell className={styles.mobileCell}>
                <div className={styles.mobileHeader}>
                    {showCheckbox && (
                        <div
                            className={styles.mobileCheckbox}
                            onClick={event => event.stopPropagation()}
                        >
                            <Checkbox
                                checked={props.selected}
                                onChange={props.onToggleSelect}
                            />
                        </div>
                    )}
                    <div className={styles.mobilePetContainer}>{petInfo}</div>
                </div>
                <div className={styles.mobileBody}>
                    {services}
                    {room}
                    <div className={styles.mobileChecksContainer}>
                        <div className={styles.mobileCheckContainer}>
                            <span className={styles.mobileCheckTitle}>Check In</span>
                            {checkIn}
                        </div>
                        <div className={styles.mobileCheckContainer}>
                            <span className={styles.mobileCheckTitle}>Check Out</span>
                            {checkOut}
                        </div>
                        <div className={styles.mobileNightsContainer}>
                            <FontAwesomeIcon icon={faMoon} />
                            {nights}
                        </div>
                    </div>
                </div>
                <div className={styles.mobileFooter}>{statusButton}</div>
            </TableCell>
        </TableRow>
    );

    return isMobile ? mobileRow : notMobileRow;
};

export default BoardingTableRow;
